import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetContent,
  BottomSheetHandle,
  BottomSheetHeader,
  BottomSheetOverlay,
  Box,
  Button,
  Heading,
  Spacing,
  Stack,
  Text,
} from "@noom/wax-component-library";

import { type SurveyAnswer, useAppContext } from "@/contexts";
import { useTrackAccessDisqualified, useTrackEvent } from "@/hooks";
import {
  ACCESS_QUALIFYING_OPTIONS,
  type AccessQualifyingOption,
  getAccessQualification,
  isAccessQualifyingOptions,
} from "@/utils/cmsAccess";
import { isCmsAccessEnabled } from "@/utils/partners";

import {
  QuestionComponentProps,
  QuestionDefinition,
  QuestionOption,
} from "../../Question";
import { CheckboxQuestion } from "../core";

const QUESTION_ID = "cmsAccessQualifyingConditions";

declare module "@/contexts" {
  interface SurveyAnswers {
    cmsAccessQualifyingConditions?: AccessQualifyingOption[];
  }
}

const AccessQualifyingQuestion: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
  surveyAnswers,
}) => {
  const { t } = useTranslation();
  const { setSurveyAnswers } = useAppContext();
  const { trackAnonEvent } = useTrackEvent();
  const trackAccessDisqualified = useTrackAccessDisqualified();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  // A single non-hypertension condition the member is asked to confirm.
  const [pendingSelection, setPendingSelection] = useState<
    AccessQualifyingOption[]
  >([]);

  useEffect(() => {
    // The checkboxes start empty, so clear the prior answer in case of back
    // navigation to keep the stored answer in sync with the screen.
    setSurveyAnswers({ ...surveyAnswers, [QUESTION_ID]: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitSelection = async (selection: AccessQualifyingOption[]) => {
    if (getAccessQualification(selection) !== "QUALIFIED") {
      trackAccessDisqualified();
    }
    await onClickNext(selection);
  };

  // `CheckboxQuestion` passes the generic `SurveyAnswer` type, so the answer is
  // narrowed to the ACCESS options before it is used.
  const handleClickNext = async (answer: SurveyAnswer) => {
    trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
      buttonName: "Continue",
      pageId: QUESTION_ID,
    });
    if (!isAccessQualifyingOptions(answer)) {
      return;
    }
    if (getAccessQualification(answer) === "NEEDS_CONFIRMATION") {
      setPendingSelection(answer);
      setIsConfirmOpen(true);
      return;
    }
    await submitSelection(answer);
  };

  const handleUpdateSelections = () => {
    trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
      buttonName: "updateSelections",
      pageId: QUESTION_ID,
    });
    setIsConfirmOpen(false);
  };

  const handleConfirmSelections = async () => {
    trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
      buttonName: "selectionsAreCorrect",
      pageId: QUESTION_ID,
    });
    setIsConfirmOpen(false);
    await submitSelection(pendingSelection);
  };

  const options: QuestionOption[] = ACCESS_QUALIFYING_OPTIONS.map((id) => ({
    id,
    text: t(`${QUESTION_ID}.options.${id}`),
  }));

  return (
    <>
      <CheckboxQuestion
        ctaText={t(`${QUESTION_ID}.ctaText`)}
        onClickBack={onClickBack}
        onClickNext={handleClickNext}
        options={options}
        questionHelperText={t(`${QUESTION_ID}.helperText`)}
        questionId={QUESTION_ID}
        questionText={t(`${QUESTION_ID}.questionText`)}
        // Previous answers are intentionally not restored: when the member comes
        // back to this screen, they answer again from scratch.
        selectedItems={[]}
      />
      {/* Same bottom sheet pattern as the Connect Health Records flow. */}
      <BottomSheet
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
      >
        <BottomSheetOverlay />
        <BottomSheetContent>
          <BottomSheetHandle />
          <BottomSheetHeader>
            <Box margin="auto" maxWidth="400px" width="100%">
              <Heading size="md">
                {t(`${QUESTION_ID}.confirmation.title`)}
              </Heading>
            </Box>
          </BottomSheetHeader>
          <BottomSheetBody>
            <Stack
              margin="auto"
              maxWidth="400px"
              spacing={Spacing[4]}
              width="100%"
            >
              <Text>{t(`${QUESTION_ID}.confirmation.message`)}</Text>
              <Button
                colorScheme="primary"
                onClick={handleUpdateSelections}
                size="xl"
                w="100%"
              >
                {t(`${QUESTION_ID}.confirmation.updateText`)}
              </Button>
              <Button
                onClick={handleConfirmSelections}
                variant="unstyled"
                w="100%"
              >
                {t(`${QUESTION_ID}.confirmation.confirmText`)}
              </Button>
            </Stack>
          </BottomSheetBody>
        </BottomSheetContent>
      </BottomSheet>
    </>
  );
};

export const Q_ACCESS_QUALIFYING: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: ({ partnerInfo }) => isCmsAccessEnabled(partnerInfo),
  component: AccessQualifyingQuestion,
};
